/**
 * Schema completeness checker for Radar.
 *
 * Validates JSON-LD structured data across multiple Schema.org types.
 * Pure HTML parsing, zero LLM calls.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface SchemaTypeResult {
  type: string
  found: boolean
  instances: number
  completeness: number // 0-100
  missingFields: string[]
  presentFields: string[]
  // false when the type has no defined requirements, so completeness is not
  // a real measurement and must be excluded from scoring and "incomplete"
  // findings. Undefined/true means it was scored against SCHEMA_REQUIREMENTS.
  evaluated?: boolean
}

export interface SchemaAuditResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: string
  pagesChecked: number
  totalSchemas: number
  schemaTypes: SchemaTypeResult[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: null
}

/**
 * Organization types that satisfy the business-identity coverage signal.
 *
 * Schema.org does not require a node to repeat every ancestor in @type, so a
 * Dentist or Restaurant node can be a valid LocalBusiness identity without
 * also declaring LocalBusiness explicitly. Keep this curated list focused on
 * common LocalBusiness branches and leaf types rather than treating every
 * unknown type with an address as a business.
 */
const BUSINESS_IDENTITY_TYPES = new Set([
  'Organization',
  'LocalBusiness',
  'ProfessionalService',
  'AccountingService',
  'Attorney',
  'AutomotiveBusiness',
  'Dentist',
  'FinancialService',
  'FoodEstablishment',
  'HomeAndConstructionBusiness',
  'Hotel',
  'LegalService',
  'LodgingBusiness',
  'MedicalClinic',
  'Pharmacy',
  'Physician',
  'RealEstateAgent',
  'Restaurant',
  'Store',
  'TravelAgency',
])

export function isBusinessIdentityType(typeKey: string): boolean {
  return BUSINESS_IDENTITY_TYPES.has(typeKey)
}

// ── Required Fields per Schema Type ─────────────────────────────────────────

const SCHEMA_REQUIREMENTS: Record<
  string,
  { required: string[]; recommended: string[] }
> = {
  Organization: {
    required: ['name', 'url'],
    recommended: ['logo', 'description', 'sameAs', 'contactPoint', 'address'],
  },
  WebSite: {
    required: ['name', 'url'],
    recommended: ['description', 'potentialAction'],
  },
  WebPage: {
    required: ['name', 'url'],
    recommended: ['description', 'breadcrumb', 'mainEntity'],
  },
  Article: {
    required: ['headline', 'author', 'datePublished'],
    recommended: [
      'dateModified',
      'image',
      'publisher',
      'description',
      'mainEntityOfPage',
    ],
  },
  FAQPage: {
    required: ['mainEntity'],
    recommended: [],
  },
  HowTo: {
    required: ['name', 'step'],
    recommended: ['description', 'image', 'totalTime'],
  },
  BreadcrumbList: {
    required: ['itemListElement'],
    recommended: [],
  },
  Product: {
    required: ['name'],
    recommended: ['description', 'image', 'brand', 'offers', 'review'],
  },
  LocalBusiness: {
    required: ['name', 'address'],
    recommended: ['telephone', 'openingHours', 'geo', 'image', 'priceRange'],
  },
  ProfessionalService: {
    required: ['name', 'url'],
    recommended: [
      'legalName',
      'description',
      'logo',
      'image',
      'sameAs',
      'address',
      'telephone',
      'email',
      'areaServed',
      'knowsAbout',
      'foundingDate',
    ],
  },
  Place: {
    required: ['name'],
    recommended: ['address', 'geo', 'url', 'image', 'description', 'telephone'],
  },
  CreativeWork: {
    required: ['name'],
    recommended: [
      'url',
      'creator',
      'datePublished',
      'dateModified',
      'description',
      'image',
      'about',
      'mainEntityOfPage',
      'inLanguage',
      'identifier',
      'keywords',
    ],
  },
  SoftwareApplication: {
    required: ['name'],
    recommended: [
      'applicationCategory',
      'operatingSystem',
      'offers',
      'description',
    ],
  },
}

// Speakable is checked as a property on entities, not a separate type

// ── Extraction ──────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Extract all JSON-LD blocks from HTML string.
 */
export function extractJsonLd(html: string): any[] {
  const blocks: any[] = []
  const regex =
    /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi
  let match

  while ((match = regex.exec(html)) !== null) {
    try {
      const parsed = JSON.parse(match[1])
      if (Array.isArray(parsed)) {
        blocks.push(...parsed)
      } else if (parsed['@graph'] && Array.isArray(parsed['@graph'])) {
        blocks.push(...parsed['@graph'])
      } else {
        blocks.push(parsed)
      }
    } catch {
      // Invalid JSON-LD, skip
    }
  }

  return blocks
}

/**
 * A field contributes to completeness only when it contains real evidence.
 * This deliberately validates shape, not Schema.org semantics: blank strings,
 * empty arrays, and recursively empty objects are absent, while non-empty
 * scalar values and containers with at least one usable value are present.
 * Work is iterative and bounded because JSON-LD comes from untrusted pages.
 */
const MAX_SCHEMA_VALUE_SCAN_NODES = 1_000
const MAX_SCHEMA_VALUE_SCAN_DEPTH = 64

function hasUsableSchemaValue(value: unknown): boolean {
  const pending: { value: unknown; depth: number }[] = [{ value, depth: 0 }]
  let visited = 0

  while (pending.length > 0 && visited < MAX_SCHEMA_VALUE_SCAN_NODES) {
    const current = pending.pop()
    if (!current) break
    visited++

    const candidate = current.value
    if (candidate === null || candidate === undefined) continue
    if (typeof candidate === 'string') {
      if (candidate.trim().length > 0) return true
      continue
    }
    if (Array.isArray(candidate)) {
      if (current.depth >= MAX_SCHEMA_VALUE_SCAN_DEPTH) continue
      const capacity = MAX_SCHEMA_VALUE_SCAN_NODES - visited - pending.length
      const childCount = Math.min(candidate.length, Math.max(0, capacity))
      for (let i = childCount - 1; i >= 0; i--) {
        pending.push({ value: candidate[i], depth: current.depth + 1 })
      }
      continue
    }
    if (typeof candidate === 'object') {
      if (current.depth >= MAX_SCHEMA_VALUE_SCAN_DEPTH) continue
      const capacity = MAX_SCHEMA_VALUE_SCAN_NODES - visited - pending.length
      if (capacity <= 0) continue

      const record = candidate as Record<string, unknown>
      const children: unknown[] = []
      for (const key in record) {
        if (!Object.prototype.hasOwnProperty.call(record, key)) continue
        children.push(record[key])
        if (children.length >= capacity) break
      }
      for (let i = children.length - 1; i >= 0; i--) {
        pending.push({ value: children[i], depth: current.depth + 1 })
      }
      continue
    }

    return true
  }

  // Exhausted/deeper-than-policy containers do not earn completeness points.
  return false
}

/**
 * Stable identity for volume deduplication. Relative identifiers resolve
 * against the document URL and URL parsing canonicalizes host casing/default
 * ports without discarding query strings or fragments, which are part of an
 * @id. JSON-LD blank-node identifiers remain document-scoped.
 */
function normalizeSchemaEntityId(
  value: unknown,
  documentUrl?: string
): string | null {
  if (typeof value !== 'string') return null
  const id = value.trim()
  if (!id) return null

  try {
    if (id.startsWith('_:')) {
      const documentIdentity = documentUrl
        ? new URL(documentUrl).href
        : 'inline-document'
      return `blank:${documentIdentity}:${id}`
    }

    return new URL(id, documentUrl || 'https://schema-audit.invalid/').href
  } catch (error) {
    void error
    return null
  }
}

/**
 * Check a single schema entity against requirements.
 */
function checkSchemaType(entity: any, typeKey: string): SchemaTypeResult {
  const reqs = SCHEMA_REQUIREMENTS[typeKey]
  if (!reqs) {
    // No requirements defined for this type. It is present and valid, but we
    // have no rubric to score it, so mark it unevaluated rather than inventing
    // a completeness number (a hardcoded 50 used to false-flag valid types
    // like Person/ItemList/TechArticle as "incomplete").
    return {
      type: typeKey,
      found: true,
      instances: 1,
      completeness: 0,
      evaluated: false,
      missingFields: [],
      presentFields: Object.keys(entity).filter(
        k => !k.startsWith('@') && hasUsableSchemaValue(entity[k])
      ),
    }
  }

  const presentFields: string[] = []
  const missingFields: string[] = []
  const fieldUsability = new Map<string, boolean>()

  for (const field of [...reqs.required, ...reqs.recommended]) {
    if (!fieldUsability.has(field)) {
      fieldUsability.set(field, hasUsableSchemaValue(entity[field]))
    }
  }

  for (const field of reqs.required) {
    if (fieldUsability.get(field)) {
      presentFields.push(field)
    } else {
      missingFields.push(field)
    }
  }

  for (const field of reqs.recommended) {
    if (fieldUsability.get(field)) {
      presentFields.push(field)
    } else {
      missingFields.push(field)
    }
  }

  // Required fields have 2x weight
  const requiredPresent = reqs.required.filter(f =>
    fieldUsability.get(f)
  ).length
  const recommendedPresent = reqs.recommended.filter(f =>
    fieldUsability.get(f)
  ).length

  const maxScore = reqs.required.length * 2 + reqs.recommended.length
  const score = requiredPresent * 2 + recommendedPresent
  const completeness = maxScore > 0 ? Math.round((score / maxScore) * 100) : 100

  return {
    type: typeKey,
    found: true,
    instances: 1,
    completeness,
    missingFields,
    presentFields,
  }
}

/**
 * Audit all JSON-LD schemas in an HTML document.
 */
export function auditSchemas(
  html: string,
  documentUrl?: string
): {
  types: SchemaTypeResult[]
  hasSpeakable: boolean
  hasPageType: boolean
  totalSchemas: number
  identifiedEntityIds: string[]
  anonymousEntityCount: number
} {
  const entities = extractJsonLd(html)
  const typeMap = new Map<string, SchemaTypeResult>()
  let hasSpeakable = false
  const identifiedEntityIds = new Set<string>()
  let anonymousEntityCount = 0

  for (const entity of entities) {
    if (!entity) continue

    const rawTypes = Array.isArray(entity['@type'])
      ? entity['@type']
      : [entity['@type']]
    const types = Array.from(
      new Set(
        rawTypes
          .filter((rawType): rawType is string => typeof rawType === 'string')
          .map(rawType =>
            rawType
              .trim()
              .replace(/^schema:/i, '')
              .trim()
          )
          .filter(Boolean)
      )
    )
    if (types.length === 0) continue

    const entityId = normalizeSchemaEntityId(entity['@id'], documentUrl)
    if (entityId) identifiedEntityIds.add(entityId)
    else anonymousEntityCount++

    for (const typeKey of types) {
      const candidate = checkSchemaType(entity, typeKey)
      const existing = typeMap.get(typeKey)
      if (!existing) {
        typeMap.set(typeKey, candidate)
      } else if (
        candidate.evaluated !== false &&
        (existing.evaluated === false ||
          candidate.completeness > existing.completeness)
      ) {
        // A type can occur more than once on one page. Represent it with the
        // most complete instance so a sparse first block does not hide richer
        // evidence later in the document, while preserving the real count.
        typeMap.set(typeKey, {
          ...candidate,
          instances: existing.instances + 1,
        })
      } else {
        existing.instances++
      }
    }

    // Check for speakable property
    if (hasUsableSchemaValue(entity.speakable)) {
      hasSpeakable = true
    }
  }

  // Foundational types surfaced as missing when absent. A LocalBusiness or
  // ProfessionalService satisfies business identity without also requiring a
  // redundant Organization node. Article and FAQPage are contextual page-type
  // options, handled by the content-page-presence check below.
  const hasBusinessIdentity = Array.from(typeMap.values()).some(
    type => type.found && isBusinessIdentityType(type.type)
  )
  const keyTypes = [
    ...(hasBusinessIdentity ? [] : ['Organization']),
    'BreadcrumbList',
    'WebSite',
  ]
  for (const t of keyTypes) {
    if (!typeMap.has(t)) {
      typeMap.set(t, {
        type: t,
        found: false,
        instances: 0,
        completeness: 0,
        missingFields: [
          ...(SCHEMA_REQUIREMENTS[t]?.required || []),
          ...(SCHEMA_REQUIREMENTS[t]?.recommended || []),
        ],
        presentFields: [],
      })
    }
  }

  // Content-page presence check. A page should carry at least one schema
  // describing what kind of page it is: Article for blog posts, WebPage for
  // landing pages, FAQPage for FAQ, etc. This is reported as a boolean
  // (hasPageType) on the result, never as a synthetic schema type. Emitting a
  // fake "ContentPage" type used to leak into the UI and LLM advisor, which
  // told users to add a nonexistent "@type: ContentPage".
  const contentPageTypes = [
    'Article',
    'BlogPosting',
    'NewsArticle',
    'TechArticle',
    'Report',
    'WebPage',
    'AboutPage',
    'CollectionPage',
    'ContactPage',
    'FAQPage',
    'HowTo',
    'ItemPage',
    'ProfilePage',
    'Product',
    'SoftwareApplication',
    'CreativeWork',
  ]
  const hasPageType = contentPageTypes.some(t => typeMap.get(t)?.found)

  return {
    types: Array.from(typeMap.values()).sort((a, b) => {
      // Found first, then by completeness
      if (a.found !== b.found) return a.found ? -1 : 1
      return b.completeness - a.completeness
    }),
    hasSpeakable,
    hasPageType,
    // Stable @id values identify one entity even when the node is declared
    // repeatedly. Id-less nodes have no safe merge key and remain distinct.
    totalSchemas: identifiedEntityIds.size + anonymousEntityCount,
    identifiedEntityIds: Array.from(identifiedEntityIds),
    anonymousEntityCount,
  }
}

/* eslint-enable @typescript-eslint/no-explicit-any */
